
import moment from "moment";
import { IFacturaMetodoPago } from "../components/facturacion/facturarCita/FacturarCitaDialog";
import IServicio from "../types/Servicio";
import { IConcepto, IImpuesto } from "../components/facturacion/facturarCita/DetalleFactura";
import IPaciente from "../types/Paciente";
import { SUPPORTED_CURRENCIES } from "../common/Constantes";

export interface IFacturaPayload {
  userId: string;
  servicioId: string;
  pacienteId: string;
  servicio: string;
  concepto: string;
  precio: number;
  impuesto: number;
  tipoFactura: "cita" | "clase" | "bono" | "agrupada";
  tipoPago: "total" | "parcial";
  facturable: boolean;
  metodoPago?: IFacturaMetodoPago | undefined;
  bonoId: string;
  devolverSesiones: boolean;
  entityId?: string | null;
}

export interface IFacturaClasePayload {
  userId: string;
  pacienteId: string;
  actividadId: number;
  month: number;
  year: number;
  facturable: boolean;
  motivoNoFacturable?: string;
  servicio: string;
  concepto: string;
  precio: number;
  impuesto: number;
  metodoPago?: IFacturaMetodoPago;
  entityId?: string | null;
}

export interface IFacturaComplementariaPayload {
  userId: string;
  pacienteId: string;
  servicio: string;
  conceptos: IConcepto[];
  impuestos: IImpuesto[];
  tipoFactura: "agrupada";
  metodoPago?: IFacturaMetodoPago | undefined;
  facturasIds: string[];
}

export interface IFacturaRectificativaPayload {
  userId: string;
  pacienteId: string;
  servicio: string;
  conceptos: IConcepto[];
  impuestos: IImpuesto[];
  tipoPago: string;
  tipoFactura: string;
  metodoPago?: IFacturaMetodoPago | undefined;
  facturaOriginalId: string;
  bonoId: string;
  devolverSesiones: boolean;
}

export interface IFacturaManualPayload {
  userId: string;
  pacienteId: string;
  servicio: string;
  concepto: string;
  precio: number;
  impuesto: number;
  metodoPago?: IFacturaMetodoPago | undefined;
  entityId?: string | null;
}

export interface IFacturaErrors {
  tipoPago?: string;
  servicio?: string;
  servicioName?: string;
  concepto?: string;
  precio?: string;
  metodoPago?: string;
  fechaPago?: string;
  entidad?: string;
  general?: string;
}

interface IConceptoErrors {
  concepto?: string;
  precio?: string;
  facturaParcialId?: string;
}

export interface IFacturaComplementariaErrors {
  paciente?: string;
  servicio?: string;
  conceptos?: IConceptoErrors[]
  metodoPago?: string;
  fechaPago?: string;
  minimoConceptos?: string;
  general?: string;
}

export const formatNumeroFactura = (numero?: string | null): string => numero || "Sin número";

// Fecha de expedición (la del PDF): la de emisión si está emitida, la de creación si no lo está (o si es una emitida
// anterior a guardar la de emisión). Misma regla que getFechaExpedicion en la API.
export const getFechaExpedicion = (factura: {
  estado?: string;
  fechaFactura?: Date | string | null;
  fechaEmision?: Date | string | null;
}): string | null => {
  const fecha = factura.estado !== "Borrador" && factura.fechaEmision ? factura.fechaEmision : factura.fechaFactura;
  const valida = fecha ? new Date(fecha) : null;
  return valida && !isNaN(valida.getTime()) ? valida.toISOString() : null;
};

export const validateFactura = ({
  tipoPago,
  selectedServicio,
  servicio,
  concepto,
  precio,
  facturable,
  facturaPagada,
  metodoPago,
  isEdit = false,
  requiereEntidad = false,
  entidadSeleccionada,
}: {
  tipoPago: "total" | "parcial";
  selectedServicio: IServicio | undefined;
  servicio: string;
  concepto: string;
  precio: number;
  facturable: boolean;
  facturaPagada: boolean;
  metodoPago?: IFacturaMetodoPago | undefined;
  isEdit?: boolean;
  requiereEntidad?: boolean;
  entidadSeleccionada?: string | null;
}): IFacturaErrors => {
  const errors: IFacturaErrors = {};

  if (!isEdit && requiereEntidad && entidadSeleccionada === undefined) {
    errors.entidad = "Debes seleccionar una entidad de facturación";
  }
  if (tipoPago !== "total" && tipoPago !== "parcial") errors.tipoPago = "Debes seleccionar un tipo de factura";
  if (!isEdit && !selectedServicio) errors.servicio = "Debes seleccionar un servicio";
  if (!servicio.trim()) errors.servicioName = "El nombre del servicio es obligatorio";
  if (!concepto.trim()) errors.concepto = "El concepto es obligatorio";
  if (facturable && precio <= 0) errors.precio = "El precio debe ser mayor que 0 si es facturable";
  if (!facturable && precio < 0) errors.precio = "El precio no puede ser negativo";
  if (facturaPagada) {
    if (!metodoPago) {
      errors.metodoPago = "Debes seleccionar un método de pago";
    } else {
      if (!metodoPago.fechaPago) {
        errors.fechaPago = "Debes seleccionar una fecha de pago";
      } else if (
        !moment.isMoment(metodoPago.fechaPago) ||
        !metodoPago.fechaPago.isValid()
      ) {
        errors.fechaPago = "Debes seleccionar una fecha de pago válida";
      }
    }
  }

  return errors;
};

export const validateFacturaComplementaria = ({
  paciente, 
  servicio,
  conceptos, 
  facturaPagada,
  metodoPago,
  isEdit = false,
  isRectificativa = false,
}: {
  servicio: IServicio | string | null;
  paciente: IPaciente | null;
  conceptos: IConcepto[],
  facturaPagada: boolean;
  metodoPago?: IFacturaMetodoPago | undefined;
  isEdit?: boolean;
  isRectificativa?: boolean;
}): IFacturaComplementariaErrors => {
  const errors: IFacturaComplementariaErrors = {};

  if (!paciente) errors.paciente = "Debes seleccionar un receptor para la factura";
  if (!isEdit && !isRectificativa) {
    if (!servicio) {
      errors.servicio = "Debes seleccionar un servicio";
    }
  } else {
    if (typeof servicio === "string") {
      if (!servicio.trim()) errors.servicio = "Debes indicar un servicio";
    } else if (!servicio) {
      errors.servicio = "Debes indicar un servicio";
    }
  }

  if (conceptos.length === 0) {
    errors.minimoConceptos = "Debes añadir al menos un concepto";
    return errors;
  }
  
  const conceptoErrors: IConceptoErrors[] = conceptos.map((c, i) => {
    const errs: IConceptoErrors = {};

    if (!c.nombre.trim()) errs.concepto = "El concepto es obligatorio";
    if (c.precio <= 0) errs.precio = "El precio debe ser mayor que 0";
    if (!isRectificativa && !c.facturaParcialId?.trim()) errs.facturaParcialId = "El ID de factura parcial es obligatorio";

    return errs;
  });

  if (conceptoErrors.some((ce) => Object.keys(ce).length > 0)) {
    errors.conceptos = conceptoErrors;
  }
  
  if (facturaPagada) {
    if (!metodoPago) {
      errors.metodoPago = "Debes seleccionar un método de pago";
    } else {
      if (!metodoPago.fechaPago) {
        errors.fechaPago = "Debes seleccionar una fecha de pago";
      } else if (
        !moment.isMoment(metodoPago.fechaPago) ||
        !metodoPago.fechaPago.isValid()
      ) {
        errors.fechaPago = "Debes seleccionar una fecha de pago válida";
      }
    }
  }

  return errors
};

export const buildFacturaPayload = ({
  userId,
  servicioId,
  pacienteId,
  servicio,
  concepto,
  precio,
  impuesto,
  tipoFactura,
  tipoPago,
  facturable,
  metodoPago,
  bonoId,
  devolverSesiones,
  entityId,
}: {
  userId: string;
  servicioId: string;
  pacienteId: string;
  servicio: string;
  concepto: string;
  precio: number;
  impuesto: number;
  tipoFactura: "cita" | "clase" | "bono" | "agrupada";
  tipoPago: "total" | "parcial";
  facturable: boolean;
  metodoPago?: IFacturaMetodoPago | undefined;
  bonoId: string;
  devolverSesiones: boolean;
  entityId?: string | null;
}): IFacturaPayload => {
  const payload: IFacturaPayload = {
    userId,
    servicioId,
    pacienteId,
    servicio,
    concepto,
    precio,
    impuesto,
    tipoFactura,
    tipoPago,
    facturable,
    metodoPago: metodoPago
      ? {
          ...metodoPago,
          nombre: bonoId && bonoId !== "" ? "Bono" : metodoPago.nombre
        }
      : undefined,
    bonoId,
    devolverSesiones,
    entityId
  };

  return payload;
};

export const buildFacturaComplementariaPayload = ({
  userId,
  pacienteId,
  servicio,
  conceptos,
  impuestos,
  metodoPago,
  facturasIds,
}: {
  userId: string;
  pacienteId: string;
  servicio: string;
  conceptos: IConcepto[];
  impuestos: IImpuesto[];
  metodoPago?: IFacturaMetodoPago | undefined;
  facturasIds: string[];
}): IFacturaComplementariaPayload => {
  const payload: IFacturaComplementariaPayload = {
    userId,
    pacienteId,
    servicio,
    conceptos,
    impuestos,
    tipoFactura: "agrupada",
    metodoPago: metodoPago || undefined,
    facturasIds,
  };

  return payload;
};

export const calcularCaducidadBono = (duracion: string): Date => {
  const ahora = new Date();

  switch (duracion) {
    case "30 días":
      ahora.setDate(ahora.getDate() + 30);
      break;

    case "6 meses":
      ahora.setMonth(ahora.getMonth() + 6);
      break;

    case "1 año":
      ahora.setFullYear(ahora.getFullYear() + 1);
      break;

    default:
      break;
  }

  return ahora;
};

export const formatCurrency = (value: number, currency?: string | null) => {
  // Redondeamos a céntimos antes de comprobar si hay parte decimal: un valor que venga de
  // Decimal128 (Mongo) casi nunca es un entero JS exacto aunque conceptualmente lo sea
  // (p.ej. 199.99999999999997), así que comparar con "value % 1" es poco fiable.
  const hasDecimals = Math.round(value * 100) % 100 !== 0;

  const importe = new Intl.NumberFormat("es-ES", {
    useGrouping: true,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);

  return `${importe} ${getCurrencySymbol(currency)}`;
};

export const agruparPorMoneda = <T extends { moneda?: string }>(items: T[]): Record<string, T[]> => {
  return items.reduce((acc, item) => {
    const moneda = item.moneda || "EUR";
    if (!acc[moneda]) acc[moneda] = [];
    acc[moneda].push(item);
    return acc;
  }, {} as Record<string, T[]>);
};

export const monedasPresentes = <T extends { moneda?: string }>(items: T[]): string[] => {
  return Array.from(new Set(items.map(item => item.moneda || "EUR"))).sort();
};

export const getCurrencySymbol = (currency?: string | null): string => {
  const code = currency || "EUR";
  const conocida = SUPPORTED_CURRENCIES.find(c => c.code === code);
  if (conocida) return conocida.symbol;

  // Fallback defensivo para una divisa fuera de la lista soportada.
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .find(part => part.type === "currency")?.value || code;
};